"use client"

import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Badge } from "../../components/ui/badge"
import { Card, CardContent, CardHeader } from "../../components/ui/card"
import { useLocation } from "react-router-dom"
import {
  ArrowLeft,
  Building2,
  MapPin,
  Briefcase,
  Clock,
  Users2,
  DollarSign,
  Globe,
  FileText,
  Target,
  Zap,
  Award,
  TrendingUp,
  GraduationCap,
  Mail,
  Phone,
  Calendar,
  Edit,
  Check,
  X,
} from "lucide-react"
import { toast } from "react-hot-toast"
import { getJobDetailsById, updateJobApprovalStatus } from "../../services/jobsapi"
import { useRbac } from "../../context"
import {
  type DashboardRestoreState,
  isDashboardRestoreState,
} from "../../utils/dashboardRestoreState"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Textarea } from "../../components/ui/textarea"

interface JobDetails {
  id: string
  title: string
  company?: {
    id: string
    name: string
  }
  companyLogoUrl?: string
  location: string
  employmentType: string
  workType: string
  status: string
  noOfOpenings: number
  createdAt: string
  updatedAt?: string
  description?: string
  requirements?: string[]
  responsibilities?: string[]
  benefits?: string[]
  salaryRange?: {
    min: number
    max: number
    currency: string
  }
  skills?: {
    requiredSkills?: string[]
    preferredSkills?: string[]
    niceToHaveSkills?: string[]
  }
  education?: string
  contactEmail?: string
  contactPhone?: string
  applicationDeadline?: string
  isRemote?: boolean
  companySize?: string
  industry?: string
  jobType?: string
  addressDetails?: {
    area?: string
    city?: string
    pincode?: string
    street?: string
  }
  assignmentHistory?: Array<{
    assignedAt?: string
    assignedBy?: { id?: string; username?: string; email?: string }
    assignedTo?: { id?: string; username?: string; email?: string }
    level?: string
  }>
  commissionAmount?: number
  commissionType?: string
  currency?: string
  hiringNeed?: string
  maxExperience?: number
  minExperience?: number
  languageRequirements?: string[]
  locationLink?: string
  minSalary?: number
  maxSalary?: number
  paymentFrequency?: string
  postedby?: { id?: string; username?: string; email?: string }
  qualificationRequirements?: string[]
  suggestedKeywordsForSeo?: string[]
  tenureInDays?: number
  isExternal?: boolean
  category?: string
  shiftTimings?: string
  minAge?: number
  maxAge?: number
  coordinators?: Array<{
    id: string
    username: string
    email: string
    phone: string
  }>
}


const ViewJobDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useRbac()
  const [jobDetails, setJobDetails] = useState<JobDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState<string>("")
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(null)
  const [rejectComment, setRejectComment] = useState("")
  const [rejectCommentError, setRejectCommentError] = useState("")
  const [isUpdatingApproval, setIsUpdatingApproval] = useState(false)

  const {currentPage, dashboardRestore: rawDashboardRestore, fromApprovals} = location.state || {} as {
    currentPage: number
    dashboardRestore?: DashboardRestoreState
    fromApprovals?: boolean
  }
  const dashboardRestore = isDashboardRestoreState(rawDashboardRestore) ? rawDashboardRestore : undefined
  const {source,selectedSHM} = location.state || {} as {source: string,selectedSHM: string}
  const isPendingApproval = (jobDetails?.status || "").toLowerCase() === "pendingapproval"
  const canReviewApproval = user?.role === "ADMIN" && isPendingApproval

  useEffect(() => {
    const fetchJobDetails = async () => {
      console.log("Received jobId:", id)
      if (!id || id === "undefined" || id.trim() === "") {
        setErrorMsg("Invalid job ID. Please check the link or try again.")
        toast.error("Invalid job ID")
        setLoading(false)
        return
      }

      try {
        const data = await getJobDetailsById(id)
        let job = data?.data?.job || (data?.data?.jobs && data.data.jobs[0])
        if (data.status === "success" && job) {
          setJobDetails(job)
        } else {
          setErrorMsg("Job not found or invalid response.")
          toast.error("Job not found or invalid response.")
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          setErrorMsg("Job not found. The job ID may be invalid or deleted.")
          toast.error("Job not found.")
        } else {
          setErrorMsg("Failed to load job details. Please try again.")
          toast.error("Failed to load job details. Please try again.")
        }
        console.error("Failed to fetch job details:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchJobDetails()
  }, [id])

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "published":
        return "bg-green-100 text-green-800"
      case "drafted":
        return "bg-yellow-100 text-yellow-800"
      case "archived":
        return "bg-gray-100 text-gray-800"
      case "pendingapproval":
        return "bg-orange-100 text-orange-800"
      case "rejected":
        return "bg-red-100 text-red-800"
      default:
        return "bg-blue-100 text-blue-800"
    }
  }

  const handleEditJob = () => {
    if (jobDetails) {
      const timestamp = Date.now()
      navigate(`/create-job?mode=edit&jobId=${jobDetails.id}&t=${timestamp}`)
    }
  }

  const handleBack = () => {
    const pageToReturn = currentPage || 1
    console.log("Navigating back to dashboard with page:", pageToReturn)
    if(source && source === "coordinations"){
      navigate('/coordinations', {state: {pageNumber: pageToReturn,selectedSHM}})
    } else {
      navigate('/dashboard', {
        state: dashboardRestore ? { dashboardRestore } : { pageNumber: pageToReturn },
      })
    }
  }

  const openApprovalConfirm = (type: "approve" | "reject") => {
    setActionType(type)
    setRejectComment("")
    setRejectCommentError("")
    setConfirmationOpen(true)
  }

  const handleConfirmApprovalAction = async () => {
    if (!jobDetails?.id || !actionType) return
    if (actionType === "reject" && !rejectComment.trim()) {
      setRejectCommentError("Rejection reason is required")
      return
    }
    setIsUpdatingApproval(true)
    try {
      if (actionType === "reject") {
        await updateJobApprovalStatus(jobDetails.id, "rejected", rejectComment.trim())
        toast.success(`Job "${jobDetails.title}" rejected successfully`)
      } else {
        await updateJobApprovalStatus(jobDetails.id, "published")
        toast.success(`Job "${jobDetails.title}" approved successfully`)
      }
      setConfirmationOpen(false)
      navigate("/dashboard", {
        state: dashboardRestore ? { dashboardRestore } : undefined,
      })
    } catch (error: any) {
      toast.error(error.response?.data?.message || `Failed to ${actionType} job`)
    } finally {
      setIsUpdatingApproval(false)
    }
  }

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} copied to clipboard!`)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        toast.success(`${type} copied to clipboard!`)
      } catch (fallbackErr) {
        toast.error('Failed to copy to clipboard')
      }
      document.body.removeChild(textArea)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading job details...</p>
        </div>
      </div>
    )
  }

  if (errorMsg || !jobDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center space-y-4">
          <p className="text-red-600 text-lg font-medium">{errorMsg || "Job not found"}</p>
          <Button onClick={handleBack} className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-6 text-gray-700 hover:text-gray-900 hover:bg-white flex items-center gap-2 rounded-lg transition-all"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Jobs
        </Button>

        {/* Header Card */}
        <Card className="bg-white shadow-xl rounded-2xl border border-gray-200 mb-6 overflow-hidden">
          <CardHeader className="p-8 border-b border-gray-100">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-start gap-6 flex-1">
                {jobDetails.isExternal ? (
                  <div className="w-20 h-20 rounded-2xl bg-orange-50 flex items-center justify-center border-2 border-orange-200">
                    <Globe className="h-10 w-10 text-orange-600" />
                  </div>
                ) : jobDetails.companyLogoUrl ? (
                  <img
                    src={jobDetails.companyLogoUrl}
                    alt={`${jobDetails.company?.name} logo`}
                    className="w-20 h-20 object-contain rounded-2xl bg-gray-50 p-3 border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center border-2 border-gray-300">
                    <Building2 className="h-10 w-10 text-gray-500" />
                  </div>
                )}
                <div className="space-y-3 flex-1">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">{jobDetails.title}</h1>
                  <div className="flex flex-wrap items-center gap-3">
                    {jobDetails.isExternal ? (
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200 px-3 py-1">
                        <Globe className="h-3 w-3 mr-1" />
                        External Job
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1">
                        <Building2 className="h-3 w-3 mr-1" />
                        {jobDetails.company?.name}
                      </Badge>
                    )}
                    <Badge className={`${getStatusBadgeColor(jobDetails.status)} font-medium px-3 py-1`}>
                      {jobDetails.status}
                    </Badge>
                    <Badge className="bg-gray-100 text-gray-800 border-gray-200 px-3 py-1">
                      <MapPin className="h-3 w-3 mr-1" />
                      {jobDetails.location}
                    </Badge>
                  </div>
                </div>
              </div>
              {user?.role === 'ADMIN' && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {canReviewApproval && (
                    <>
                      <Button
                        variant="outline"
                        size="lg"
                        className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50 shadow-sm font-medium"
                        onClick={() => openApprovalConfirm("approve")}
                      >
                        <Check className="h-5 w-5" /> Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="lg"
                        className="flex items-center gap-2 border-red-300 text-red-700 hover:bg-red-50 shadow-sm font-medium"
                        onClick={() => openApprovalConfirm("reject")}
                      >
                        <X className="h-5 w-5" /> Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="lg"
                    className="flex items-center gap-2 bg-orange-500 text-white border-0 hover:bg-orange-600 shadow-lg font-medium"
                    onClick={handleEditJob}
                    aria-label={canReviewApproval || fromApprovals ? "Review Changes" : "Edit Job"}
                  >
                    <Edit className="h-5 w-5" /> {canReviewApproval || fromApprovals ? "Review Changes" : "Edit Job"}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Stats */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <Users2 className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{jobDetails.noOfOpenings}</p>
                    <p className="text-xs text-gray-600 mt-1">Openings</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Briefcase className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-gray-900">{jobDetails.employmentType}</p>
                    <p className="text-xs text-gray-600 mt-1">Type</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <Globe className="h-6 w-6 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-gray-900">{jobDetails.workType}</p>
                    <p className="text-xs text-gray-600 mt-1">Work Mode</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <Clock className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-gray-900">{jobDetails.hiringNeed || "N/A"}</p>
                    <p className="text-xs text-gray-600 mt-1">Hiring Need</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Description */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <FileText className="h-5 w-5 text-orange-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Job Description</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div
                  className="prose prose-sm max-w-none text-gray-700 leading-relaxed
                    [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-1
                    [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:space-y-1
                    [&_li]:mb-1
                    [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
                    [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3
                    [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-2
                    [&_p]:mb-3
                    [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600
                    [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono
                    [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto
                    [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800
                    [&_strong]:font-bold
                    [&_em]:italic
                    [&_u]:underline
                    [&_s]:line-through"
                  dangerouslySetInnerHTML={{ __html: jobDetails.description || "No description provided." }}
                />
              </CardContent>
            </Card>

            {/* Skills & Requirements */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Target className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Skills & Requirements</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* Required Skills */}
                {jobDetails.skills?.requiredSkills && jobDetails.skills.requiredSkills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-orange-600" />
                      Required Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobDetails.skills.requiredSkills.map((skill, index) => (
                        <Badge key={`required-${index}`} className="bg-orange-100 text-orange-800 border-orange-200 px-3 py-1.5 font-medium">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preferred Skills */}
                {jobDetails.skills?.preferredSkills && jobDetails.skills.preferredSkills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Award className="h-4 w-4 text-blue-600" />
                      Preferred Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobDetails.skills.preferredSkills.map((skill, index) => (
                        <Badge key={`preferred-${index}`} className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1.5 font-medium">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nice to Have Skills */}
                {jobDetails.skills?.niceToHaveSkills && jobDetails.skills.niceToHaveSkills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      Nice to Have
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobDetails.skills.niceToHaveSkills.map((skill, index) => (
                        <Badge key={`nice-${index}`} className="bg-green-100 text-green-800 border-green-200 px-3 py-1.5 font-medium">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {!jobDetails.skills?.requiredSkills?.length &&
                 !jobDetails.skills?.preferredSkills?.length &&
                 !jobDetails.skills?.niceToHaveSkills?.length && (
                  <p className="text-gray-500 text-center py-4">No skills specified.</p>
                )}

                {/* Experience & Education */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                  <div className="flex items-start gap-3">
                    <Briefcase className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Experience</p>
                      <p className="text-gray-900">{jobDetails.minExperience !== undefined ? `${jobDetails.minExperience} - ${jobDetails.maxExperience} years` : "Not specified"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <GraduationCap className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Education</p>
                      <p className="text-gray-900">{jobDetails.qualificationRequirements?.join(", ") || "Not specified"}</p>
                    </div>
                  </div>
                </div>

                {/* Age Range */}
                {(jobDetails.minAge || jobDetails.maxAge) && (
                  <div className="flex items-start gap-3 pt-2">
                    <Users2 className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Age Range</p>
                      <p className="text-gray-900">{jobDetails.minAge} - {jobDetails.maxAge} years</p>
                    </div>
                  </div>
                )}

                {/* Languages */}
                {jobDetails.languageRequirements && jobDetails.languageRequirements.length > 0 && (
                  <div className="flex items-start gap-3 pt-2">
                    <Globe className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Languages Required</p>
                      <p className="text-gray-900">{jobDetails.languageRequirements.join(", ")}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SEO Keywords */}
            {jobDetails.suggestedKeywordsForSeo && jobDetails.suggestedKeywordsForSeo.length > 0 && (
              <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
                <CardHeader className="border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Target className="h-5 w-5 text-yellow-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">SEO Keywords</h2>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {jobDetails.suggestedKeywordsForSeo.map((keyword, index) => (
                      <Badge key={index} className="bg-yellow-50 text-yellow-800 border-yellow-200 px-3 py-1.5 font-medium">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Salary & Compensation */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Compensation</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
                  <p className="text-sm text-gray-600 mb-1">Salary Range</p>
                  <p className="text-2xl font-bold text-green-700">
                    {jobDetails.minSalary && jobDetails.maxSalary 
                      ? `₹ ${jobDetails.minSalary.toLocaleString()} - ${jobDetails.maxSalary.toLocaleString()}`
                      : "Not specified"}
                  </p>
                  {jobDetails.paymentFrequency && (
                    <p className="text-sm text-gray-600 mt-1">per {jobDetails.paymentFrequency}</p>
                  )}
                </div>

                {!jobDetails.isExternal && user?.role !== 'HR' && jobDetails.commissionAmount !== undefined && jobDetails.commissionAmount > 0 && (
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-100">
                    <p className="text-sm text-gray-600 mb-1">Commission</p>
                    <p className="text-xl font-bold text-orange-700">
                      {jobDetails.commissionType === "fixed"
                        ? `₹ ${((jobDetails.commissionAmount / 100) * 70).toFixed(2)} Per Joining`
                        : `${((jobDetails.commissionAmount / 100) * 50).toFixed(2)}% of Annual CTC`}
                    </p>
                  </div>
                )}

                {!jobDetails.isExternal && user?.role !== 'HR' && jobDetails.tenureInDays !== undefined && jobDetails.tenureInDays > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">Tenure</p>
                      <p className="text-lg font-semibold text-gray-900">{jobDetails.tenureInDays} Days</p>
                    </div>
                  </div>
                )}

                {jobDetails.shiftTimings && (
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <Clock className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Shift Timings</p>
                      <p className="text-lg font-semibold text-gray-900">{jobDetails.shiftTimings}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Company & Location */}
            {!jobDetails.isExternal && (
              <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
                <CardHeader className="border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Company Details</h2>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Company Name</p>
                    <p className="text-lg font-semibold text-gray-900">{jobDetails.company?.name || "Not specified"}</p>
                  </div>

                  {(jobDetails.addressDetails?.area || jobDetails.addressDetails?.city || jobDetails.addressDetails?.pincode || jobDetails.addressDetails?.street) && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Address</p>
                          <div className="space-y-1 text-gray-700">
                            {jobDetails.addressDetails?.street && <p>{jobDetails.addressDetails.street}</p>}
                            {jobDetails.addressDetails?.area && <p>{jobDetails.addressDetails.area}</p>}
                            {jobDetails.addressDetails?.city && <p>{jobDetails.addressDetails.city}</p>}
                            {jobDetails.addressDetails?.pincode && <p>PIN: {jobDetails.addressDetails.pincode}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {jobDetails.locationLink && (
                    <a
                      href={jobDetails.locationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium transition-colors"
                    >
                      <MapPin className="h-4 w-4" />
                      View on Map
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Job Metadata */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-gray-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Job Information</h2>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Posted On</span>
                  <span className="font-medium text-gray-900">
                    {new Date(jobDetails.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </div>
                {jobDetails.updatedAt && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Last Updated</span>
                    <span className="font-medium text-gray-900">
                      {new Date(jobDetails.updatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                )}
                {jobDetails.category && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Category</span>
                    <Badge className="bg-gray-100 text-gray-800 font-medium">{jobDetails.category}</Badge>
                  </div>
                )}
                {jobDetails.applicationDeadline && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600">Deadline</span>
                    <span className="font-medium text-red-600">
                      {new Date(jobDetails.applicationDeadline).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Coordinator Information */}
            <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
              <CardHeader className="border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-gray-900">Coordinators</h2>
              </CardHeader>
              <CardContent className="p-6">
                {jobDetails.coordinators && jobDetails.coordinators.length > 0 ? (
                  <div className="space-y-4">
                    {jobDetails.coordinators.map((coordinator, index) => (
                      <div key={coordinator.id || index} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                        <p className="font-medium text-gray-900 mb-2">{coordinator.username || 'Coordinator'}</p>
                        <div className="space-y-1">
                          {coordinator.email && (
                            <div 
                              className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors"
                              onClick={() => copyToClipboard(coordinator.email, 'Email')}
                              title="Click to copy email"
                            >
                              <Mail className="h-3.5 w-3.5 text-gray-400" />
                              <p className="text-sm text-gray-600">{coordinator.email}</p>
                            </div>
                          )}
                          {coordinator.phone && (
                            <div 
                              className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors"
                              onClick={() => copyToClipboard(coordinator.phone, 'Phone number')}
                              title="Click to copy phone number"
                            >
                              <Phone className="h-3.5 w-3.5 text-gray-400" />
                              <p className="text-sm text-gray-600">{coordinator.phone}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">No coordinators assigned</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assignment History */}
            {jobDetails.assignmentHistory && jobDetails.assignmentHistory.length > 0 && (
              <Card className="bg-white shadow-lg rounded-xl border border-gray-200">
                <CardHeader className="border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Users2 className="h-5 w-5 text-indigo-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Assignment History</h2>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {jobDetails.assignmentHistory.map((assignment, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-600">Assigned At</p>
                            <p className="font-medium text-gray-900">
                              {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleDateString("en-GB") : "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Level</p>
                            <Badge className="mt-1">{assignment.level || "N/A"}</Badge>
                          </div>
                          <div>
                            <p className="text-gray-600">Assigned By</p>
                            <p className="font-medium text-gray-900">{assignment.assignedBy?.username || "N/A"}</p>
                            <p className="text-xs text-gray-500">{assignment.assignedBy?.email || ""}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Assigned To</p>
                            <p className="font-medium text-gray-900">{assignment.assignedTo?.username || "N/A"}</p>
                            <p className="text-xs text-gray-500">{assignment.assignedTo?.email || ""}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 flex">
          <Button
            variant="outline"
            onClick={handleBack}
            className="text-gray-700 hover:text-gray-900 hover:bg-white flex items-center gap-2 px-6 py-3 rounded-lg shadow-md transition-all"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Jobs
          </Button>
        </div>
      </div>

      <Dialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {actionType === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {actionType === "approve"
                ? `Are you sure you want to approve "${jobDetails.title}"?`
                : `Are you sure you want to reject "${jobDetails.title}"? Please provide a reason.`}
            </DialogDescription>
          </DialogHeader>
          {actionType === "reject" && (
            <div className="space-y-2">
              <Textarea
                placeholder="Rejection reason"
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value)
                  if (rejectCommentError) setRejectCommentError("")
                }}
                className="min-h-[100px]"
              />
              {rejectCommentError && (
                <p className="text-sm text-red-600">{rejectCommentError}</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmationOpen(false)} disabled={isUpdatingApproval}>
              Cancel
            </Button>
            <Button
              variant={actionType === "approve" ? "default" : "destructive"}
              onClick={handleConfirmApprovalAction}
              disabled={isUpdatingApproval}
              className={actionType === "approve" ? "bg-green-600 hover:bg-green-700" : ""}
            >
              {isUpdatingApproval ? "Saving..." : actionType === "approve" ? "Approve" : "Reject"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ViewJobDetails
